import { FC, useContext, useMemo } from "react";

import { RadioGroup } from "capitalrx-components-2.0/lib/forms";
import { Button, Table, useSimpleTableDataSource } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Heading, Semibold, Subtitle } from "capitalrx-components-2.0/lib/typography";

import { ProviderManagement } from "@/api/interfaces";
import { getDocumentsByType } from "@/components/Account/Update/components";
import { PROVIDER_MANAGEMENT_LAYOUT } from "@/constants";
import { ThemeSelectorContext } from "@/context";
import { getThemeButton } from "@/utils";


export interface DetailsViewProps {
  readonly provider: ProviderManagement.ProviderSchema;
  readonly setProvider: Function;
  readonly documents: ProviderManagement.DocumentSchema[];
  setIsEditDetails: Function;
}

export const DetailsView: FC<DetailsViewProps> = ({ provider, setProvider, documents, setIsEditDetails }) => {
  const { four } = useResponsive(PROVIDER_MANAGEMENT_LAYOUT);
  const { primary_color } = useContext(ThemeSelectorContext);

  const documentsByType = useMemo(() => {
        return getDocumentsByType(documents);
      }, [documents]);

  const w9Rows = useMemo(() => {
    return documents
      .filter((doc) => doc.document_type === "w9")
      .map((doc) => ({
        id: doc.id.toString(),
        data: {
          name: doc.file_name,
          created_at: new Date(doc.created_at).toLocaleDateString(),
          notes: doc.notes || "—",
        },
      }));
  }, [documents]);

  const W9Datasource = useSimpleTableDataSource(w9Rows);

  const handleEditClick = () => {
    setIsEditDetails(true);
  };


  return (
    <Stack spacing="large">
      <Shelf>
        <Subtitle>Provider Details</Subtitle>
        <Button appearance={getThemeButton("tertiary", primary_color)} icon="edit" onClick={handleEditClick}>Edit</Button>
        <Shelf.Spacer />
      </Shelf>
      <Stack>
        <Heading>Identification</Heading>
        <Grid>
          <Grid.Span columns={four}>
            <Stack spacing="mini">
              <Semibold>TIN (Tax Identification Number)</Semibold>
              <Body>{provider?.tax_id_number || "--"}</Body>
            </Stack>
          </Grid.Span>
          <Grid.Span columns={four}>
            <Stack spacing="mini">
              <Semibold>Designation</Semibold>
              <RadioGroup hideLabel value={provider?.tax_id_number_designation} orientation="horizontal" readOnly>
                <RadioGroup.Option value={ProviderManagement.DESIGNATION_TYPE_VALUES.SSN}>SSN</RadioGroup.Option>
                <RadioGroup.Option value={ProviderManagement.DESIGNATION_TYPE_VALUES.EIN}>EIN</RadioGroup.Option>
              </RadioGroup>
            </Stack>
          </Grid.Span>
        </Grid>
        <Grid>
          <Grid.Span columns={four}>
            <Stack spacing="mini">
              <Semibold>NPI (Billing)</Semibold>
              <Body>{provider?.billing_npi || "--"}</Body>
            </Stack>
          </Grid.Span>
          <Grid.Span columns={four}>
            <Stack spacing="mini">
              <Semibold>Medicare ID Number</Semibold>
              <Body>{provider?.medicare_id_number || "--"}</Body>
            </Stack>
          </Grid.Span>
          <Grid.Span columns={four}>
          <Stack spacing="mini">
            <Semibold>Medicaid ID Number</Semibold>
            <Body>{provider?.medicaid_id_number || "--"}</Body>
          </Stack>
          </Grid.Span>
        </Grid>
      </Stack>
      <Stack>
        <Heading>Business Details</Heading>
        <Grid>
          <Grid.Span columns={four}>
            <Stack spacing="mini">
              <Semibold>Provider Name (Legal Business Name)</Semibold>
              <Body>{provider?.legal_business_name || "--"}</Body>
            </Stack>
          </Grid.Span>
           <Grid.Span columns={four}>
            <Stack spacing="mini">
              <Semibold>Doing Business As (DBA) Name</Semibold>
              <Body>{provider?.doing_business_as_name || "--"}</Body>
            </Stack>
          </Grid.Span>
        </Grid>
        {provider?.type === ProviderManagement.PROVIDER_TYPE_VALUES.PHARMACY && (
          <Grid>
            <Grid.Span columns={four}>
              <Stack spacing="mini">
                <Semibold>NCPDP ID (NABP)</Semibold>
                <Body>{provider?.pharmacy_nabp_id || "--"}</Body>
              </Stack>
            </Grid.Span>
            <Grid.Span columns={four}>
              <Stack spacing="mini">
                <Semibold>Relationship ID (Chain Code)</Semibold>
                <Body>{provider?.pharmacy_chain_code || "--"}</Body>
              </Stack>
            </Grid.Span>
          </Grid>
        )}
      </Stack>
      <Stack>
        <Heading>Physical Address</Heading>
        <Grid>
          <Grid.Span columns={four}>
            <Stack spacing="mini">
              <Semibold>Address</Semibold>
                {provider?.physical_address_line_1 && (
                  <Stack spacing="none">
                  <Body>{provider?.physical_address_line_1}</Body>
                  <Body>{provider?.physical_address_line_2}</Body>
                  <Body>{provider?.physical_address_city}, {provider?.physical_address_state} {provider?.physical_address_zip_code}</Body>
                  </Stack>
                )}
                {!provider?.physical_address_line_1 && <Body>--</Body>}
            </Stack>
          </Grid.Span>
        </Grid>
      </Stack>
      <Stack>
        <Heading>Mailing Address</Heading>
        <Grid>
          <Grid.Span columns={four}>
            <Stack spacing="mini">
              <Semibold>Address</Semibold>
                {provider?.mailing_address_line_1 && (
                  <Stack spacing="none">
                  <Body>{provider?.mailing_address_line_1}</Body>
                  <Body>{provider?.mailing_address_line_2}</Body>
                  <Body>{provider?.mailing_address_city}, {provider?.mailing_address_state} {provider?.mailing_address_zip_code}</Body>
                  </Stack>
                )}
                {!provider?.mailing_address_line_1 && <Body>--</Body>}
            </Stack>
          </Grid.Span>
        </Grid>
      </Stack>
      <Stack>
        <Heading>W-9 Form</Heading>
        {documentsByType.w9 && (
          <Table dataSource={W9Datasource}>
            <Table.Text name="name" title="Name" initialWidth="span" />
            <Table.Text name="created_at" title="Date Added" />
            <Table.Text name="notes" title="Notes" initialWidth="span" />
          </Table>
        )}
      </Stack>
    </Stack>
  );
};
